import { QueryHookOptions, useQuery } from '@apollo/client';
import { DocumentNode } from 'graphql';
import { get } from 'lodash';
import { useEffect, useMemo } from 'react';
import { useHistory } from 'react-router';
import { IQueryWithPagination, OrderEnum } from 'types/common';
import { getGraphQLQueryName, getNewQueryString } from 'utils';

import { useQueryParamsAsKeyValuePair } from './useQueryParams';

export interface IQueryWithSearch {
  graphQLQuery: DocumentNode;
  defaultOrder?: Record<string, OrderEnum>;
  defaultPage?: number;
  mapQueryToVariables?: (query: Record<string, any> | null) => Record<string, any> | null;
  queryProps?: QueryHookOptions;
}

export type WithKey<K extends string | number | symbol, ValueType> = {
  [k in K]: ValueType;
};

export default function useReactiveQuery<D = unknown>({
  graphQLQuery,
  defaultOrder = {
    createdAt: OrderEnum.DESC,
  },
  defaultPage = 1,
  mapQueryToVariables,
  queryProps = {},
}: IQueryWithSearch) {
  const queryName = getGraphQLQueryName(graphQLQuery) || '';

  const queryParams = useQueryParamsAsKeyValuePair();
  const history = useHistory();

  const query = useMemo(() => {
    let order = defaultOrder;
    const pageQuery = get(queryParams, 'page');
    const page = pageQuery ? parseFloat(pageQuery) : defaultPage;

    const orderQuery = get(queryParams, 'order');
    if (orderQuery) {
      try {
        order = JSON.parse(orderQuery);
      } catch (err) {
        // Ignore
      }
    }

    return {
      ...(queryParams || {}),
      page,
      order,
    };
  }, [queryParams]);

  const variables = {
    ...query,
    pagination: { page: query.page },
    order: query.order,
    ...(mapQueryToVariables?.(queryParams) || {}),
    ...queryProps?.variables,
  };

  const { loading, data: queryResult, refetch, fetchMore } = useQuery<
    WithKey<typeof queryName, IQueryWithPagination<D[]>>
  >(graphQLQuery, {
    ...queryProps,
    variables,
  });

  const { data, meta } = queryResult?.[queryName] || {};

  const onChangeQuery = (param: Record<string, unknown>) => {
    const queryString = history.location.search;
    const { pathname } = history.location;
    const newQueryString = getNewQueryString(queryString, param);
    history.push({ pathname, search: newQueryString });
  };

  useEffect(() => {
    if (!!data && !loading) {
      refetch();
    }
  }, [queryParams]);

  return {
    loading,
    refetch,
    fetchMore,
    meta,
    data,
    query,
    onChangeQuery,
  };
}
